import { apiSlice } from '../../../api/apiSlice';
import type { Borefield, CreateBorefieldDTO, UpdateBorefieldDTO, CreateBoreDTO, UpdateBoreDTO } from '../types';

export const borefieldApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get all borefields
    getBorefields: builder.query<Borefield[], void>({
      query: () => '/borefields',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Borefield' as const, id })),
              { type: 'Borefield', id: 'LIST' },
            ]
          : [{ type: 'Borefield', id: 'LIST' }],
    }),

    // Get single borefield by ID
    getBorefield: builder.query<Borefield, string>({
      query: (id) => `/borefields/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Borefield', id }],
    }),

    // Create new borefield
    createBorefield: builder.mutation<Borefield, CreateBorefieldDTO>({
      query: (data) => ({
        url: '/borefields',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Borefield', id: 'LIST' }],
    }),

    // Update existing borefield
    updateBorefield: builder.mutation<Borefield, UpdateBorefieldDTO>({
      query: ({ id, ...data }) => ({
        url: `/borefields/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Borefield', id },
        { type: 'Borefield', id: 'LIST' },
      ],
      // Optimistic update
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          borefieldApi.util.updateQueryData('getBorefield', id, (draft) => {
            Object.assign(draft, patch);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Delete borefield
    deleteBorefield: builder.mutation<void, string>({
      query: (id) => ({
        url: `/borefields/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Borefield', id },
        { type: 'Borefield', id: 'LIST' },
      ],
    }),

    // Add bore to borefield
    addBore: builder.mutation<Borefield, { borefieldId: string; bore: CreateBoreDTO }>({
      query: ({ borefieldId, bore }) => ({
        url: `/borefields/${borefieldId}/bores`,
        method: 'POST',
        body: bore,
      }),
      invalidatesTags: (_result, _error, { borefieldId }) => [
        { type: 'Borefield', id: borefieldId },
      ],
    }),

    // Update bore in borefield
    updateBore: builder.mutation<Borefield, { borefieldId: string; boreId: string; bore: UpdateBoreDTO }>({
      query: ({ borefieldId, boreId, bore }) => ({
        url: `/borefields/${borefieldId}/bores/${boreId}`,
        method: 'PUT',
        body: bore,
      }),
      invalidatesTags: (_result, _error, { borefieldId }) => [
        { type: 'Borefield', id: borefieldId },
      ],
    }),

    // Delete bore from borefield
    deleteBore: builder.mutation<void, { borefieldId: string; boreId: string }>({
      query: ({ borefieldId, boreId }) => ({
        url: `/borefields/${borefieldId}/bores/${boreId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { borefieldId }) => [
        { type: 'Borefield', id: borefieldId },
      ],
    }),
  }),
});

export const {
  useGetBorefieldsQuery,
  useGetBorefieldQuery,
  useCreateBorefieldMutation,
  useUpdateBorefieldMutation,
  useDeleteBorefieldMutation,
  useAddBoreMutation,
  useUpdateBoreMutation,
  useDeleteBoreMutation,
} = borefieldApi;
